import React from 'react';
import { MessageSquare, Radio, Heart, User } from 'lucide-react';

interface RadialMenuProps {
    x: number;
    y: number;
    onClose: () => void;
    onMessage: () => void;
    onPulse: () => void;
    onHeart: () => void;
    onProfile: () => void;
}

export const RadialMenu: React.FC<RadialMenuProps> = ({ x, y, onClose, onMessage, onPulse, onHeart, onProfile }) => {
    const radius = 80; // distance from center to icons in px
    const items = [
        { icon: <MessageSquare size={20} />, action: onMessage, label: 'Message' },
        { icon: <Radio size={20} />, action: onPulse, label: 'Pulse' },
        { icon: <Heart size={20} />, action: onHeart, label: 'Heart' },
        { icon: <User size={20} />, action: onProfile, label: 'Profile' },
    ];

    return (
        <div
            className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/20 backdrop-blur-sm touch-none"
            onPointerUp={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div
                className="absolute"
                style={{
                    left: x,
                    top: y,
                    transform: 'translate(-50%, -50%)' // Center the menu origin on the coordinates
                }}
                onPointerUp={(e) => e.stopPropagation()}
            >
                {/* Central Close Button / Hub (Optional visual anchor) */}
                <div className="absolute inset-0 w-2 h-2 bg-[var(--neon-cyan)] rounded-full -translate-x-1/2 -translate-y-1/2 shadow-[0_0_10px_var(--neon-cyan)]" />

                {items.map((item, i) => {
                    const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2; // Start from top
                    const ix = Math.cos(angle) * radius;
                    const iy = Math.sin(angle) * radius;

                    return (
                        <button
                            key={i}
                            className="absolute w-12 h-12 flex items-center justify-center rounded-full glass-panel transition-all duration-300 hover:scale-110 hover:bg-[var(--neon-cyan)]/20 active:scale-95 group pointer-events-auto"
                            style={{
                                left: `${ix}px`,
                                top: `${iy}px`,
                                transform: 'translate(-50%, -50%)'
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            onPointerUp={(e) => {
                                e.stopPropagation();
                                item.action();
                                onClose();
                            }}
                        >
                            <div className="text-[var(--neon-cyan)] group-hover:text-white transition-colors">
                                {item.icon}
                            </div>
                            {/* Optional Label Tooltip */}
                            <span className="absolute -bottom-6 text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-black/50 px-1.5 py-0.5 rounded">
                                {item.label}
                            </span>
                        </button>
                    );
                })}

                {/* Connecting Lines Effect (Optional decoration) */}
                <svg className="absolute top-0 left-0 w-0 h-0 overflow-visible pointer-events-none opacity-30">
                    {items.map((_, i) => {
                        const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2;
                        const ix = Math.cos(angle) * (radius - 24); // Stop short of button
                        const iy = Math.sin(angle) * (radius - 24);
                        return (
                            <line
                                key={i}
                                x1={0}
                                y1={0}
                                x2={ix}
                                y2={iy}
                                stroke="var(--neon-cyan)"
                                strokeWidth="1"
                                strokeDasharray="4 2"
                            />
                        );
                    })}
                </svg>

            </div>
        </div>
    );
};

export default RadialMenu;
